/**
 * Context provider that wires the reducer + localStorage + undo/redo stack
 * and exposes a single `useBuilder()` hook to the rest of the builder.
 */

import React from 'react';

import type { Action } from './reducer';
import { reducer } from './reducer';
import { saveDraft } from './persistence';
import type { DefinitionState } from '~/pages/workflows/builder/types';

interface BuilderContextValue {
  state: DefinitionState;
  dispatch: (action: Action) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  selectedNodeId: string | null;
  setSelectedNodeId: (id: string | null) => void;
  /**
   * Local-only focus request sent from validation banner → inspector to
   * scroll the offending field into view.
   */
  focusField: { nodeId: string; fieldKey: string } | null;
  setFocusField: (f: { nodeId: string; fieldKey: string } | null) => void;
}

const BuilderContext = React.createContext<BuilderContextValue | null>(null);

const MAX_HISTORY = 50;

interface HistoryState {
  past: DefinitionState[];
  present: DefinitionState;
  future: DefinitionState[];
}

type HistoryAction = Action | { type: 'undo' } | { type: 'redo' };

function isReplaceOrTrivial(action: Action): boolean {
  return action.type === 'replace-state';
}

function historyReducer(history: HistoryState, action: HistoryAction): HistoryState {
  if (action.type === 'undo') {
    const previous = history.past[history.past.length - 1];
    if (!previous) return history;
    return {
      past: history.past.slice(0, -1),
      present: previous,
      future: [history.present, ...history.future]
    };
  }
  if (action.type === 'redo') {
    const next = history.future[0];
    if (!next) return history;
    return {
      past: [...history.past, history.present],
      present: next,
      future: history.future.slice(1)
    };
  }
  const nextPresent = reducer(history.present, action);
  if (nextPresent === history.present) return history;
  if (isReplaceOrTrivial(action)) {
    return { past: [], present: nextPresent, future: [] };
  }
  const past = [...history.past, history.present].slice(-MAX_HISTORY);
  return { past, present: nextPresent, future: [] };
}

export function BuilderProvider(props: {
  initialState: DefinitionState;
  children: React.ReactNode;
}) {
  const [history, dispatch] = React.useReducer(historyReducer, {
    past: [],
    present: props.initialState,
    future: []
  });
  const [selectedNodeId, setSelectedNodeId] = React.useState<string | null>(null);
  const [focusField, setFocusField] = React.useState<{ nodeId: string; fieldKey: string } | null>(
    null
  );

  // Autosave on every change. Intentionally simple — localStorage is cheap.
  React.useEffect(() => {
    saveDraft(history.present);
  }, [history.present]);

  const value: BuilderContextValue = {
    state: history.present,
    dispatch: dispatch as unknown as (action: Action) => void,
    undo: () => dispatch({ type: 'undo' } as HistoryAction as Action),
    redo: () => dispatch({ type: 'redo' } as HistoryAction as Action),
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    selectedNodeId,
    setSelectedNodeId,
    focusField,
    setFocusField
  };

  return <BuilderContext.Provider value={value}>{props.children}</BuilderContext.Provider>;
}

export function useBuilder(): BuilderContextValue {
  const ctx = React.useContext(BuilderContext);
  if (!ctx) throw new Error('useBuilder must be inside BuilderProvider');
  return ctx;
}
